import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import TaskList from "../../components/tasks/TaskList";
import projectService from "../../services/projectService";
import { FaTasks, FaSearch, FaPlus, FaClipboardCheck, FaHourglassHalf, FaCheckCircle, FaLayerGroup, FaArrowLeft } from "react-icons/fa";
import "../../styles/taskManagement.css";

const tabs = [
  { key: "all", label: "All Tasks", icon: <FaLayerGroup className="mr-2" /> },
  { key: "pending", label: "Pending", icon: <FaHourglassHalf className="mr-2" /> },
  { key: "in_progress", label: "In Progress", icon: <FaTasks className="mr-2" /> },
  { key: "completed", label: "Completed", icon: <FaCheckCircle className="mr-2" /> },
];

const ProjectTasksPage = () => {
  const { projectId } = useParams();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        // Check if projectId is valid before making the request
        if (!projectId || projectId === 'undefined') {
          console.error('Invalid project ID:', projectId);
          setLoading(false);
          return;
        }
        
        const projectData = await projectService.getProjectById(projectId);
        if (projectData) {
          // Make sure the project object has a consistent ID field
          const normalizedProject = {
            ...projectData,
            // Ensure we always have an _id field for compatibility
            _id: projectData.id || projectData._id
          };
          setProject(normalizedProject);
        } else {
          console.error('Project data not found');
        }
      } catch (error) {
        console.error("Error fetching project details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, [projectId]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="loading-text">Loading project details...</p>
      </div>
    );
  }

  if (!project && !loading) {
    return (
      <div className="error-container">
        <h3>Project not found</h3>
        <p>The project you're looking for doesn't exist or you don't have access to it.</p>
        <Link to="/tasks" className="btn btn-primary">
          <FaArrowLeft className="mr-2" /> Back to Tasks
        </Link>
      </div>
    );
  }

  return (
    <div className="task-container">
      {/* Header with modern gradient background */}
      <div className="task-header-gradient">
        <div className="task-header-pattern"></div>
        <div style={{position: 'relative', zIndex: 10, maxWidth: '1200px', margin: '0 auto'}}>
          <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '1.5rem'}}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <div style={{background: 'rgba(255, 255, 255, 0.2)', padding: '0.75rem', borderRadius: '1rem', marginRight: '1rem'}}>
                <FaClipboardCheck style={{color: 'white', fontSize: '1.75rem'}} />
              </div>
              <div>
                <Link to="/tasks" className="header-back-link">
                  <FaArrowLeft style={{marginRight: '0.75rem', color: 'white'}} />
                </Link>
              </div>
            </div>
            {(currentUser?.role === "admin" || currentUser?.role === "manager") && (
              <Link
                to={`/tasks/new?projectId=${projectId}`}
                className="btn btn-primary"
                style={{display: 'flex', alignItems: 'center', background: 'white', color: 'var(--primary-dark)'}}
              >
                <FaPlus style={{marginRight: '0.5rem'}} /> Add Task to Project
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div>
        <div className="search-container">
          <div className="search-input-wrapper">
            <div className="search-input-icon">
              <FaSearch />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search tasks in ${project.name}...`}
              className="search-input"
            />
          </div>
        </div>

        {/* Tabs with icons */}
        <div className="tabs-container">
          <nav className="tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`tab-button ${activeTab === tab.key ? 'tab-button-active' : ''}`}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <span className="tab-icon">{tab.icon}</span>
                  {tab.label}
                </div>
                {activeTab === tab.key && (
                  <div className="tab-button-active-indicator" />
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Task List */}
        <div
          key={activeTab}
          className="tab-content"
        >
          <div className="tab-content-header">
            <h2 className="tab-content-title">
              <span className="tab-content-title-icon">
                {tabs.find(tab => tab.key === activeTab)?.icon}
              </span>
              {activeTab === "all" 
                ? `All ${project.name} Tasks` 
                : activeTab === "pending" 
                ? `Pending ${project.name} Tasks` 
                : activeTab === "in_progress" 
                ? `In Progress ${project.name} Tasks` 
                : `Completed ${project.name} Tasks`}
            </h2>
          </div>
          <TaskList projectId={projectId} filter={activeTab !== "all" ? activeTab : null} search={search} />
        </div>
      </div>
    </div>
  );
};

export default ProjectTasksPage;
